import { IntegrationApi } from '@alliander-fe/api';
import { useQuery } from '@tanstack/react-query';

import { useRouter } from 'next/router';
import { IntegrationApiFeaturesCongestionReportsContractsResponsesGetCongestionReportsDetailsResponse as CongestionReportDetails } from '@alliander-fe/api';
import { useSitecoreContext } from '@liander/context';
/**
 * This hook is used to get the congestion report details.
 * It will first check if the congestion report is already provided.
 * If not, it will fetch the congestion report details from the API.
 * @param congestionReport - The congestion report details.
 * @param isLoading - Whether the congestion report is loading.
 * @param isError - Whether the congestion report is in error.
 * @returns The congestion report details.
 */
export const useCongestionDetails = (
  congestionReport?: CongestionReportDetails,
  isLoading?: boolean,
  isError?: boolean,
  toggleBandWidth?: boolean
) => {
  const router = useRouter();
  const { isEditor } = useSitecoreContext();
  const reportStation = router.query.path ? router.query.path[router.query.path.length - 2] : '';
  const reportCongestionType = router.query.path
    ? router.query.path[router.query.path.length - 1]
    : '';

  const isEnabled =
    !isLoading &&
    !congestionReport &&
    Boolean(reportStation) &&
    Boolean(reportCongestionType) &&
    !isEditor;

  const {
    isLoading: isLoadingCongestionReport,
    isError: isErrorCongestionReport,
    data: congestionReportData,
  } = useQuery({
    queryKey: ['congestionReport', reportStation, reportCongestionType],
    retry: false,
    enabled: isEnabled,
    queryFn: () =>
      IntegrationApi.congestionReportsEndpointsGetCongestionReportsDetails({
        name: reportStation,
        congestionType: reportCongestionType,
      }),
    select: (data) => {
      if (!toggleBandWidth) {
        return {
          ...data,
          resolvedStartDate: undefined,
          resolvedStartDateMainAsset: undefined,
          underlyingAssets:
            data.underlyingAssets?.map((asset) => ({
              ...asset,
              expectedEndDateStart: undefined,
            })) || [],
        };
      }

      return data;
    },
  });

  return {
    isLoading: isEnabled ? isLoadingCongestionReport : isLoading,
    isError: isEnabled ? isErrorCongestionReport : isError,
    congestionReport: isEnabled ? congestionReportData : congestionReport,
  };
};
